import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { UntypedFormGroup, UntypedFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { CalcConfigService } from '_services/calc-config.service';
import { UnivPrefsService } from '_services/univ-prefs.service';
import { Package } from '../../_models/package';
import { Preferences } from '../../_models/preferences';
import { MatCard, MatCardHeader, MatCardTitle, MatCardSubtitle, MatCardContent } from '@angular/material/card';
import { MatFormField, MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';

const FUNDING_CATEGORIES = [
  { formKey: 'institutionalScholarship', parentId: 'InstitutionalScholarship', label: 'Institutional Scholarship', nameDefault: 'Institutional Scholarship', hasCap: true },
  { formKey: 'institutionalGrant', parentId: 'InstitutionalGrant', label: 'Institutional Grant', nameDefault: 'Institutional Grant', hasCap: true },
  { formKey: 'stateGrant', parentId: 'StateGrant', label: 'State Grant', nameDefault: 'State Grant', hasCap: true },
  { formKey: 'studentLoan', parentId: 'StudentLoan', label: 'Student Loan', nameDefault: null, hasCap: true },
  { formKey: 'schoolDefinedFund1', parentId: 'SchoolDefinedFund1', label: 'School Defined Fund 1', nameDefault: 'School Defined Fund 1', hasCap: true },
  { formKey: 'schoolDefinedFund2', parentId: 'SchoolDefinedFund2', label: 'School Defined Fund 2', nameDefault: 'School Defined Fund 2', hasCap: true },
  { formKey: 'consolidatedSelfHelp', label: 'Consolidated Self-Help', nameDefault: 'Consolidated Self Help', hasCap: false },
] as const;

type FundingCategory = typeof FUNDING_CATEGORIES[number];

@Component({
    selector: 'app-package-overrides',
    templateUrl: './package-overrides.component.html',
    styleUrls: ['./package-overrides.component.css'],
    imports: [ReactiveFormsModule, MatCard, MatCardHeader, MatCardTitle, MatCardSubtitle, MatCardContent, MatFormField, MatInput, MatButton]
})
export class PackageOverridesComponent implements OnInit {
  readonly categories = FUNDING_CATEGORIES;
  poForm: UntypedFormGroup;
  state: 'loading' | 'synced' | 'modified' | 'error' = 'loading';
  schoolId: string;

  private savedValue: ReturnType<PackageOverridesComponent['buildFormValue']> | null = null;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private fb: UntypedFormBuilder,
    public cc: CalcConfigService,
    private ups: UnivPrefsService,
    private route: ActivatedRoute) {
    this.poForm = this.fb.group(
      Object.fromEntries(
        FUNDING_CATEGORIES.map(category => [category.formKey, this.fb.group(this.controlsFor(category))])
      )
    );
    this.schoolId = this.route.snapshot.parent?.paramMap.get('schoolId') ?? '';
  }

  ngOnInit() {
    this.cc.packages.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.tryPatch());

    this.ups.draftPrefs.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.tryPatch());

    this.poForm.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (this.state !== 'loading') {
        this.state = 'modified';
      }
    });

    this.tryPatch();
  }

  getRowHeaderId(formKey: string): string {
    return `caps-row-${formKey}`;
  }

  getFieldLabelIds(field: 'name' | 'cap' | 'floor', formKey: string): string {
    const columnHeaderId = field === 'name' ? 'caps-name-header' : field === 'cap' ? 'caps-cap-header' : 'caps-floor-header';
    return `${columnHeaderId} ${this.getRowHeaderId(formKey)}`;
  }

  async save() {
    if (!this.poForm.valid || (this.state !== 'modified' && this.state !== 'error')) {
      return;
    }

    try {
      const names = this.displayNames();
      await this.ups.savePreferences(names, true);
      await this.ups.savePreferences(names, false);
      this.applyOverridesToPackages(this.cc.packages.value);
      this.cc.savePackages(this.schoolId, this.cc.packages.value);
      this.savedValue = this.poForm.getRawValue();
      this.poForm.markAsPristine();
      this.state = 'synced';
    } catch (err) {
      console.log(err);
      this.state = 'error';
    }
  }

  cancel() {
    if (!this.savedValue || (this.state !== 'modified' && this.state !== 'error')) {
      return;
    }
    this.poForm.patchValue(this.savedValue, { emitEvent: false });
    this.poForm.markAsPristine();
    this.state = 'synced';
  }

  private controlsFor(category: FundingCategory): Record<string, string | null> {
    const controls: Record<string, string | null> = {};
    if (category.nameDefault) {
      controls['name'] = '';
    }
    if (category.hasCap) {
      controls['cap'] = null;
      controls['floor'] = null;
    }
    return controls;
  }

  private tryPatch() {
    if (this.state === 'modified' || this.state === 'error') {
      return;
    }
    const prefs = this.ups.draftPrefs.value;
    if (this.cc.loadedDraftSchoolId !== this.schoolId || this.ups.loadedSchoolId !== this.schoolId || !prefs) {
      return;
    }

    this.savedValue = this.buildFormValue(this.cc.packages.value, prefs);
    this.poForm.patchValue(this.savedValue, { emitEvent: false });
    this.poForm.markAsPristine();
    this.state = 'synced';
  }

  private buildFormValue(packages: Package[], prefs: Preferences) {
    const parentById = new Map(
      packages.filter(pack => pack.parent).map(pack => [pack.parent.id, pack.parent])
    );

    return Object.fromEntries(
      FUNDING_CATEGORIES.map(category => {
        const value: { name?: string; cap?: number | null; floor?: number | null } = {};
        if (category.nameDefault) {
          const stored = prefs[category.formKey];
          value.name = typeof stored === 'string' ? stored : '';
        }
        if (category.hasCap && 'parentId' in category) {
          const parent = parentById.get(category.parentId);
          value.cap = parent?.cap ?? null;
          value.floor = parent?.floor ?? null;
        }
        return [category.formKey, value];
      })
    );
  }

  private displayNames(): Partial<Preferences> {
    const names: Partial<Preferences> = {};
    for (const category of FUNDING_CATEGORIES) {
      if (!category.nameDefault) {
        continue;
      }
      const raw = this.poForm.get([category.formKey, 'name'])?.value;
      names[category.formKey] = typeof raw === 'string' ? raw.trim() : '';
    }
    return names;
  }

  private normalizeOverrideValue(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
  }

  private applyOverridesToPackages(packages: Package[]): void {
    for (const category of FUNDING_CATEGORIES) {
      if (!category.hasCap || !('parentId' in category)) {
        continue;
      }
      const group = this.poForm.get(category.formKey)?.value ?? {};
      const cap = this.normalizeOverrideValue(group.cap);
      const floor = this.normalizeOverrideValue(group.floor);

      for (const pack of packages) {
        if (pack.parent?.id === category.parentId) {
          pack.parent.cap = cap;
          pack.parent.floor = floor;
        } else if (!pack.parent && pack.id === category.parentId) {
          pack.parent = { id: category.parentId, cap, floor };
        }
      }
    }
  }
}
